import { Button, Select } from '@civilgrid/ui/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { assignExtractTemplate, type ExtractTemplate } from '~/api/extract';
import type { OrganizationDetail } from '~/common/types';
import { Notify } from '~/components2/Notify';
import OrganizationPicker from '~/components2/OrganizationPicker';
import { MajorHeading } from '../common/Heading';
import { buildAssignTemplateBody, canSubmitAssignTemplate, EXTRACT_ADMIN_STATE_QUERY_KEY } from './common';

interface Props {
  templates: ExtractTemplate[];
}

export default function AssignExtractTemplate({ templates }: Props) {
  const [extractTemplateId, setExtractTemplateId] = useState('');
  const [organization, setOrganization] = useState<OrganizationDetail | null>(null);
  const [allOrgs, setAllOrgs] = useState(false);
  const queryClient = useQueryClient();

  const formState = { extractTemplateId, organizationId: organization?.id ?? null, allOrgs };

  const mutation = useMutation({
    mutationFn: () => assignExtractTemplate(buildAssignTemplateBody(formState)),
    onSuccess: (res) => {
      Notify.info({
        title: 'Assign Extract Template',
        message: `Assigned to ${res.assigned} org(s)${res.skipped ? ` (${res.skipped} already assigned)` : ''}.`,
      });
      setExtractTemplateId('');
      setOrganization(null);
      setAllOrgs(false);
      queryClient.invalidateQueries({ queryKey: EXTRACT_ADMIN_STATE_QUERY_KEY });
    },
    onError: () => {
      Notify.error({
        title: 'Assign Extract Template',
        message: 'An error occurred while assigning the template. Check logs for details.',
      });
    },
  });

  const canSubmit = canSubmitAssignTemplate(formState) && !mutation.isPending;

  return (
    <div className="flex flex-col items-start space-y-4">
      <MajorHeading tooltip="Links an extract template to a single organization or to all organizations. Re-assigning an already-linked org is a no-op.">
        Assign Extract Template
      </MajorHeading>

      <div className="flex w-full max-w-md flex-col items-start space-y-4">
        <Select
          label="Template"
          isRequired
          placeholder="Select a template"
          value={extractTemplateId}
          onValueChange={(value) => setExtractTemplateId(value ?? '')}
          options={templates.map((template) => ({ value: template.id, label: template.name }))}
          data-testid="extract-assign-template-select"
        />

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={allOrgs}
            onChange={(e) => setAllOrgs(e.target.checked)}
            data-testid="extract-assign-all-orgs-checkbox"
          />
          Assign to all organizations
        </label>

        {!allOrgs && (
          <OrganizationPicker
            selectedOrganization={organization}
            onOrganizationSelect={(_id, org) => setOrganization(org)}
            placeholder="Select organization"
          />
        )}

        <Button
          onClick={() => mutation.mutate()}
          loading={mutation.isPending}
          disabled={!canSubmit}
          data-testid="extract-assign-submit"
        >
          Assign Template
        </Button>
      </div>
    </div>
  );
}
